import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, AlertOctagon, Copy, GitBranch, GitMerge, Database, DatabaseBackup, List, Info, Activity, Search, Shield, ShieldCheck, Key, Pause, Play, History, Bookmark, RotateCcw, CopyPlus, CopyMinus, GitBranchPlus, Trash2 } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import supabaseIcon from '../assets/icons/supabase-logo-icon.svg';

const POLL_INTERVAL_OPTIONS = [
  { value: 60, label: '60 seconds' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
];

const formatPollInterval = (seconds?: number): string => {
  if (!seconds) return '5 minutes';
  if (seconds % 60 === 0) {
    const mins = seconds / 60;
    return mins === 1 ? '1 minute' : `${mins} minutes`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
};

// generateWebhookSecret produces a random URL-safe signing secret the user
// pastes into Supabase's Database Webhook header. Generated client-side so the
// user never has to invent one; the same value is stored on connect and used to
// verify each delivery.
function generateWebhookSecret(): string {
  const bytes = new Uint8Array(24);
  (window.crypto || (window as any).msCrypto).getRandomValues(bytes);
  const s = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  return `whsec_${s}`;
}

const SupabaseIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleRepo, setExampleRepo] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);
  const [resourcesTracked, setResourcesTracked] = useState(0);
  const [pollInterval, setPollInterval] = useState(300);
  const [webhookConfigured, setWebhookConfigured] = useState(false);
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const [accessToken, setAccessToken] = useState('');
  const [apiUrl, setApiUrl] = useState('');
  // A signing secret is generated for the user to paste into Supabase's
  // Database Webhook header, rather than asking them to invent one.
  const [webhookSecret] = useState(() => generateWebhookSecret());
  const [connectPollInterval, setConnectPollInterval] = useState(300);

  const webhookUrl = `${window.location.origin}/api/webhooks/supabase`;

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getSupabaseStatus();
      if (data?.connected) {
        setConnected(true);
        setLastPollAt(data.last_poll_at || null);
        setResourcesTracked(data.resources_tracked || 0);
        setPollInterval(data.poll_interval || 300);
        setWebhookConfigured(!!data.webhook_configured);
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectSupabase({
        access_token: accessToken,
        api_url: apiUrl || undefined,
        webhook_secret: webhookSecret || undefined,
        poll_interval: connectPollInterval,
      });
      setSuccessMsg('Supabase connected successfully!');
      setAccessToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectSupabase();
      setConnected(false);
      setSuccessMsg('Supabase disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testSupabase();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Supabase (${data.project_count ?? 0} project${data.project_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copySecret = () => {
    navigator.clipboard.writeText(webhookSecret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {/* BODY_PLACEHOLDER */}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={supabaseIcon} alt="Supabase" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Supabase
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Project, branch, backup, and read-replica lifecycle, network restrictions, and investigation automation</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Resources Tracked</span>
                <p className="font-medium">{resourcesTracked.toLocaleString()} resource{resourcesTracked !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Poll Interval</span>
                <p className="font-medium">{formatPollInterval(pollInterval)}</p>
              </div>
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Database Webhook</span>
                <p className="font-medium">{webhookConfigured ? `Configured (${webhookEventCount} event${webhookEventCount !== 1 ? 's' : ''})` : 'Not configured'}</p>
              </div>
              {lastWebhookAt && (
                <div>
                  <span className="text-muted-foreground">Last Webhook</span>
                  <p className="font-medium">{new Date(lastWebhookAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Database Webhook (optional)</CardTitle>
            <CardDescription>Register a Supabase Database Webhook to trigger workflows on table INSERT/UPDATE/DELETE events. Control-plane triggers (health, backups, branches) work via polling without this.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-sm">Webhook URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={copyWebhookUrl} className="gap-1 shrink-0">
                  {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                In the Supabase dashboard, open <strong className="text-foreground">Database → Webhooks</strong>, create a webhook on the table(s) you want to watch, set this URL, and add an HTTP header <code className="bg-background px-1 rounded">X-Supabase-Webhook-Secret</code> matching the signing secret you set when connecting.
              </p>
            </div>
          </CardContent>
        </Card>
        {/* BLOCKS_PLACEHOLDER */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Supabase actions and triggers in your workflows to automate database provisioning, CI/CD, incident response, cost, and security.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Project Health Degraded', desc: 'Triggers when a project service (db, auth, rest, realtime, storage, pooler) is unhealthy.', Icon: AlertOctagon },
                  { name: 'Backup Failed', desc: 'Triggers when a project\u2019s most recent database backup failed.', Icon: AlertTriangle },
                  { name: 'Read Replica Unhealthy', desc: 'Triggers when a project read replica is unhealthy.', Icon: AlertOctagon },
                  { name: 'Usage/Quota Threshold', desc: 'Triggers when a project crosses a usage/quota threshold.', Icon: Activity },
                  { name: 'Branch Created', desc: 'Triggers when a new database branch is created.', Icon: GitBranchPlus },
                  { name: 'Branch Migration Failed', desc: 'Triggers when a database branch enters a failed migration state.', Icon: GitMerge },
                  { name: 'Database Row Event', desc: 'Triggers on table INSERT/UPDATE/DELETE via a Supabase Database Webhook.', Icon: Database },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'List Projects', desc: 'List projects with their status and region.', Icon: List },
                  { name: 'Get Project', desc: 'Fetch a project\u2019s status, region, and metadata.', Icon: Info },
                  { name: 'Get Project Health', desc: 'Check per-service health of a project.', Icon: Activity },
                  { name: 'Create Branch', desc: 'Create an ephemeral preview database branch.', Icon: GitBranchPlus },
                  { name: 'List Branches', desc: 'List a project\u2019s database branches.', Icon: List },
                  { name: 'Get Branch', desc: 'Fetch a branch config and connection ref.', Icon: Info },
                  { name: 'Merge Branch', desc: 'Merge a branch\u2019s migrations into its parent.', Icon: GitMerge },
                  { name: 'Reset Branch', desc: 'Reset a branch to its parent\u2019s state.', Icon: RotateCcw },
                  { name: 'Delete Branch', desc: 'Permanently delete a branch (gate behind Approval).', Icon: Trash2 },
                  { name: 'List Backups', desc: 'List backups and PITR availability.', Icon: DatabaseBackup },
                  { name: 'Create Restore Point', desc: 'Create a named checkpoint before a migration.', Icon: Bookmark },
                  { name: 'Restore Backup (PITR)', desc: 'Point-in-time restore (gate behind Approval).', Icon: History },
                  { name: 'Setup Read Replica', desc: 'Provision a read replica in a region.', Icon: CopyPlus },
                  { name: 'Remove Read Replica', desc: 'Remove a read replica (gate behind Approval).', Icon: CopyMinus },
                  { name: 'Pause Project', desc: 'Pause a project to reduce cost.', Icon: Pause },
                  { name: 'Restore Project', desc: 'Un-pause a paused project.', Icon: Play },
                  { name: 'Get Network Restrictions', desc: 'Read a project\u2019s allowed CIDRs.', Icon: Shield },
                  { name: 'Update Network Restrictions', desc: 'Lock down a project\u2019s allowed CIDRs.', Icon: ShieldCheck },
                  { name: 'List API Keys', desc: 'List API key names/types (no secret values).', Icon: Key },
                  { name: 'Investigate Supabase', desc: 'AI investigates projects, health, branches, and backups.', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a pull request opens in{' '}
              <input
                value={exampleRepo}
                onChange={e => setExampleRepo(e.target.value)}
                placeholder="org/repo"
                style={{ width: `${Math.max(exampleRepo.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />, create a Supabase preview database branch, and post the branch connection details to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack. When the PR closes, delete the branch.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleRepo || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a pull request opens in ${exampleRepo}, create a Supabase preview database branch and post the branch connection details to ${exampleChannel} on Slack. When the PR closes, delete the branch.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>{/* CONNECT_PLACEHOLDER */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={supabaseIcon} alt="Supabase" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>Supabase</CardTitle>
                <CardDescription>Connect Supabase for project, branch, backup, and read-replica lifecycle, network restrictions, and investigation automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Personal Access Token</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to Supabase with a <strong className="text-foreground">Personal Access Token</strong>, sent as a Bearer token to <strong className="text-foreground">https://api.supabase.com</strong> to read project, branch, backup, and health state and perform control-plane actions. Create one at <strong className="text-foreground">Account → Access Tokens</strong> in the Supabase dashboard.
              </p>
              <Input
                id="supabase-token"
                type="password"
                value={accessToken}
                onChange={e => setAccessToken(e.target.value)}
                placeholder="sbp_..."
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the Supabase Management API.
              </p>
            </div>
            <div>
              <Label htmlFor="supabase-api-url" className="text-sm">API URL (optional)</Label>
              <Input
                id="supabase-api-url"
                value={apiUrl}
                onChange={e => setApiUrl(e.target.value)}
                placeholder="https://api.supabase.com"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Leave blank to use the default Supabase Management API host.</p>
            </div>
            <div>
              <Label htmlFor="supabase-poll" className="text-sm">Poll Interval</Label>
              <select
                id="supabase-poll"
                value={connectPollInterval}
                onChange={e => setConnectPollInterval(Number(e.target.value))}
                className="mt-1 w-full sm:w-auto block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {POLL_INTERVAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Supabase has no tenant-wide control-plane webhook, so Kestrel polls the Management API to detect project health, backup, read-replica, and branch events. Faster intervals detect incidents sooner; the minimum is 60 seconds.
              </p>
            </div>
            <div>
              <Label className="text-sm">Database Webhook (optional)</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Optional — only needed for the <strong className="text-foreground">Database Row Event</strong> trigger (workflows that fire on table INSERT/UPDATE/DELETE). All other Supabase triggers (project health, backups, read replicas, branches) are detected by polling and need no webhook. Skip this if you don't plan to use row-level triggers.
              </p>
              <div className="rounded-md border border-input bg-muted/40 p-4 mb-3">
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                    <span>In the <strong className="text-foreground">Supabase dashboard</strong>, open your project and go to <strong className="text-foreground">Integrations → Database Webhooks</strong> (or <strong className="text-foreground">Database → Webhooks</strong>), then click <strong className="text-foreground">Create a new hook</strong>.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                    <span>Name the hook, select the <strong className="text-foreground">table</strong> to watch, and choose the <strong className="text-foreground">events</strong> (Insert / Update / Delete). For <strong className="text-foreground">Type</strong>, choose <strong className="text-foreground">HTTP Request</strong> with method <strong className="text-foreground">POST</strong>.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                    <div className="min-w-0 flex-1">
                      <span>Set the <strong className="text-foreground">URL</strong> to:</span>
                      <div className="relative mt-1.5">
                        <pre className="text-xs font-mono bg-background border border-input rounded px-3 py-2 pr-10 overflow-x-auto whitespace-pre">{webhookUrl}</pre>
                        <button
                          type="button"
                          onClick={copyWebhookUrl}
                          aria-label="Copy webhook URL"
                          className="absolute top-1.5 right-1.5 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        >
                          {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                    <div className="min-w-0 flex-1">
                      <span>Under <strong className="text-foreground">HTTP Headers</strong>, add a header named <code className="bg-background px-1 rounded">X-Supabase-Webhook-Secret</code> with this generated value, then click <strong className="text-foreground">Create webhook</strong>:</span>
                      <div className="relative mt-1.5">
                        <pre className="text-xs font-mono bg-background border border-input rounded px-3 py-2 pr-10 overflow-x-auto whitespace-pre">{webhookSecret}</pre>
                        <button
                          type="button"
                          onClick={copySecret}
                          aria-label="Copy webhook secret"
                          className="absolute top-1.5 right-1.5 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        >
                          {secretCopied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">Kestrel generated this secret for you — Kestrel stores it on connect and verifies every delivery against it. You don't need to type it anywhere else.</p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
            <Button onClick={handleConnect} disabled={saving || !accessToken}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Supabase
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default SupabaseIntegration;
